class KlaviyoSubscription extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));

    this.iti = null;

    this.initBirthdateInput();
    this.initPhoneInput();
  }

  initPhoneInput() {
    const phoneInput = this.querySelector("input[name='phone']");
    if (!phoneInput) return;

    const formRow = phoneInput.closest(".form-row");
    const utilsUrl = formRow?.dataset.intlTelInputUtilsUrl;
    if (!utilsUrl) return;

    if (!window.intlTelInput) {
      window.addEventListener("load", () => this.initPhoneInput(), { once: true });
      return;
    }

    this.iti = window.intlTelInput(phoneInput, {
      loadUtils: () => import(utilsUrl),
      initialCountry: formRow?.dataset.currentCountryCode || "de"
    });
  }

  initBirthdateInput() {
    const birthdateInput = this.querySelector("input[name='birthdate']");
    if (!birthdateInput) return;
    birthdateInput.setAttribute('maxlength', '10');
    birthdateInput.addEventListener('input', function (e) {
      const isDeleting = e.inputType === 'deleteContentBackward' || e.inputType === 'deleteContentForward';
      let digits = this.value.replace(/\D/g, '').slice(0, 8);
      let formatted = '';
      if (digits.length > 4) {
        formatted = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
      } else if (digits.length > 2) {
        formatted = digits.slice(0, 2) + '/' + digits.slice(2);
      } else {
        formatted = digits;
      }
      if (isDeleting && (this.value.endsWith('/') || formatted.length < this.value.length)) {
        formatted = formatted.slice(0, -1);
      }
      this.value = formatted;
    });
  }

  handleSubmit(e) {
    e.preventDefault();

    this.resetForm();

    let isValid = false;
    try {
      isValid = this.validateForm();
    } catch (e) {
      console.error(e);
    }
    if (!isValid) return;

    this.setLoading(true);
    const formData = Object.fromEntries(new FormData(this.form));
    const user = { email: formData.email };
    const phoneNumber = this.iti ? this.iti.getNumber() : formData.phone;
    if (phoneNumber) user["phone_number"] = phoneNumber;
    if (formData.birthdate) {
      const [day, month, year] = formData.birthdate.split("/");
      user["properties"] = { birthdate: `${year}-${month}-${day}` };
    }

    const subscription = {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: user,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: this.form.dataset.klaviyoListId,
            },
          },
        },
    };

    if (this.source) {
      subscription.attributes["custom_source"] = this.source;
    }

    fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${window.KLAVIYO_API_KEY}`,
      {
        method: "POST",
        headers: {
          revision: "2026-04-15",
          accept: 'application/vnd.api+json',
          "content-type": "application/vnd.api+json"
        },
        body: JSON.stringify({
          data: subscription
        }),
      }
    ).then(async (response) => {
        if (response.status === 202) {
          localStorage.setItem("theme:popup-filled", "true");
          this.toggleFormSuccessMessage(true);
        } else {
          this.toggleFormErrorMessage(true);
        }
      })
      .catch((err) => {
        console.log(err);
        this.toggleFormErrorMessage(true);
        setTimeout(() => {
          this.toggleFormErrorMessage(false);
        }, 3000);
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  validateForm() {
    const fields = [this.querySelector("input[name='email']"), this.querySelector("input[name='phone']"), this.querySelector("input[name='birthdate']")].filter(Boolean);

    for (const input of fields) {
      // Check if required fields are empty
      if (input.required && input.value === "") {
        this.showInputError(input, input.closest(".form-row")?.getAttribute("data-error-empty"));
        this.scrollToField(input);
        return false;
      }

      // Validate email format
      if (input.name === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(input.value)) {
          this.showInputError(input, input.closest(".form-row")?.getAttribute("data-error-invalid"));
          this.scrollToField(input);
          return false;
        }
      }

      // Validate phone number format
      if (input.value && input.name === "phone" && this.iti) {
        if (!this.iti.isValidNumber()) {
          this.showInputError(input, input.closest(".form-row")?.getAttribute("data-error-invalid"));
          this.scrollToField(input);
          return false;
        }
      }

      // Validate birthdate format (DD/MM/YYYY)
      if (input.value && input.name === "birthdate") {
        const birthdateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!birthdateRegex.test(input.value)) {
          this.showInputError(input, input.closest(".form-row")?.getAttribute("data-error-invalid"));
          this.scrollToField(input);
          return false;
        }
        const [day, month, year] = input.value.split("/").map(Number);
        const entered = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (entered.getTime() > today.getTime()) {
          this.showInputError(input, input.closest(".form-row")?.getAttribute("data-error-invalid"));
          this.scrollToField(input);
          return false;
        }
      }
    }

    return true;
  }

  showInputError(input, errorMessage) {
    try {
      input.classList.add("form-error");
      input.setAttribute("aria-invalid", "true");
      this.toggleFormErrorMessage(true, errorMessage);
    } catch (e) {
      console.error(e);
    }
  }

  clearInputError(input) {
    try {
      input.classList.remove("form-error");
      input.removeAttribute("aria-invalid");
      this.hideAllMessages();
    } catch (e) {
      console.error(e);
    }
  }

  resetForm() {
    const inputFields = [...this.querySelectorAll("input")];
    inputFields.forEach((input) => this.clearInputError(input));

    // Hide all messages
    this.hideAllMessages();
  }

  scrollToField(input) {
    if (!input) {
      return;
    }

    input.focus();

    input.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }

  setLoading(val) {
    const button = this.querySelector(`button[form="${this.formId}"][type="submit"]`);
    button.toggleAttribute("disabled", val);
  }

  hideAllMessages() {
    this.querySelector(".success_message").classList.add("hidden");
    this.querySelector(".error_message").classList.add("hidden");
  }

  toggleFormSuccessMessage(show) {
    this.querySelector(".success_message").classList.toggle("hidden", !show);
    this.querySelector(".error_message").classList.toggle("hidden", show);
    // Hide all other form elements except the success message
    if (show) {
      [...this.children].forEach((child) => {
        if (child !== this.querySelector(".success_message")) {
          child.classList.add("hidden");
        }
      });
    }
  }

  toggleFormErrorMessage(show, message = "") {
    this.querySelector(".success_message").classList.toggle("hidden", show);
    this.querySelector(".error_message").classList.toggle("hidden", !show);
    const error_message = message || this.form.dataset.errorMessage;
    this.querySelector(".error_message").querySelector(".banner").innerText = error_message;
  }

  get formId() {
    return this.form.id;
  }

  get source() {
    return this.form.dataset.source;
  }
}

if (!customElements.get("klaviyo-subscription")) {
  customElements.define("klaviyo-subscription", KlaviyoSubscription);
}
